import React, { useRef } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Flex, Grid, Inline, xcss } from '@atlaskit/primitives';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import UFOSegment from '@atlaskit/react-ufo/segment';

import { ProductKeys } from '../../../common/constants/products';
import { NewIssueNavigatorCTA } from '../../../common/ui/new-issue-navigator-cta';
import { Hide } from '../../../common/ui/width-observer';
import { serveJiraNinFpsNavigation } from '../../../common/utils/experiments/experiment-exports';
import { ErrorBoundary } from '../../../controllers/error-boundary';
import {
	useBootstrapLoading,
	useFeatures,
	usePrimaryProduct,
} from '../../../controllers/store/bootstrap';

import AppFilterInline from './app-filter-inline';
import { LoadingFilters } from './loading-filters';
import ProductFilters from './product-filters';

const filterRowStyles = xcss({
	overflow: 'hidden',
	position: 'relative',
	paddingBlock: 'space.050',
	paddingInline: 'space.050',
});

const Filters = () => {
	const [bootstrapLoading] = useBootstrapLoading();
	const [features] = useFeatures();
	const filterContainerRef = useRef<HTMLDivElement>();
	const [primaryProduct] = usePrimaryProduct();

	if (bootstrapLoading) {
		return (
			<UFOLoadHold name="search-page-filters-loading">
				<LoadingFilters />
			</UFOLoadHold>
		);
	}

	const productFiltersWithClear = (
		<Inline space="space.100" ref={filterContainerRef}>
			<UFOSegment name="search-page-product-filters">
				<ProductFilters containerRef={filterContainerRef} />
			</UFOSegment>
		</Inline>
	);

	// Side by side always gets the "responsive below sm" version
	if (features?.sideBySide?.enabled) {
		return (
			<Flex direction="column" gap="space.200">
				<UFOSegment name="search-page-sbs-app-filter-inline">
					<AppFilterInline />
				</UFOSegment>
				{productFiltersWithClear}
			</Flex>
		);
	}

	return (
		<Grid templateColumns="1fr auto" alignItems="center" gap="space.150">
			<Flex direction="column" gap="space.200" xcss={filterRowStyles}>
				<Hide target="layout" above="sm">
					<UFOSegment name="search-page-app-filter-inline">
						<AppFilterInline />
					</UFOSegment>
				</Hide>
				{productFiltersWithClear}
			</Flex>
			{(serveJiraNinFpsNavigation({ primaryProduct })?.cohort === 'control' ||
				!serveJiraNinFpsNavigation({ primaryProduct })) &&
				primaryProduct === ProductKeys.Jira && <NewIssueNavigatorCTA />}
		</Grid>
	);
};

const FiltersWithErrorBoundary = (): React.JSX.Element => {
	return (
		<ErrorBoundary section="SearchPageFilters">
			<Filters />
		</ErrorBoundary>
	);
};

export default FiltersWithErrorBoundary;
